import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { getErpUserId } from '../auth/erp-auth-token.util';
import { getEnterpriseStationIdFromLocalStorage } from '../enterprise-context';
import { WarehouseService } from '../warehouse/warehouse.service';
import { buildErpEntrepotPayloadFromLcForm } from '../warehouse/erp-lc-entrepot-payload.util';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';

export interface LogicielCaisseEntrepotOption {
  id: string;
  label: string;
}

export interface EntrepotRow {
  id: number;
  libelle?: string | null;
  user_id?: number;
  station_id?: number;
}

/** Ligne tableau « Gestion des entrepôts » (ERP). */
export interface EntrepotTableRow {
  id: string;
  'CODE ENTREPÔT': string;
  LIBELLÉ: string;
  VILLE: string;
  PAYS: string;
  /** Ordre réassort (1, 2, …) si défini en base ; sinon « — ». */
  'PRIORITÉ'?: string;
  is_reassort?: boolean;
  is_globale?: boolean;
}

export interface EntrepotFormPayload {
  libelle: string;
  ville: string;
  pays: string;
  code_entrepot: string | null;
  is_reassort: boolean;
  is_globale: boolean;
}

export interface EntrepotCaisseOption {
  id: number;
  numero_de_caisse: number;
  entrepot_id: number;
  entrepot_libelle?: string;
  uuid?: string | null;
  has_uuid?: boolean;
  label?: string;
}

/** Entrepôt renvoyé après création / mise à jour (API). */
export interface EntrepotApiRecord {
  id: number;
  libelle?: string | null;
  ville?: string | null;
  pays?: string | null;
  code_entrepot?: string | null;
  station_id?: number | null;
  user_id?: number | null;
  is_reassort?: boolean | number | null;
  is_globale?: boolean | number | null;
  erp_id?: number | null;
}

interface EntrepotMutationResponse {
  success: boolean;
  entrepot?: Record<string, unknown>;
  error?: string;
}

interface EntrepotsListResponse {
  success: boolean;
  entrepots?: EntrepotRow[];
  error?: string;
}

interface EntrepotsErpListResponse {
  success: boolean;
  items?: EntrepotTableRow[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseEntrepotService {
  private entrepotOptionsCache$: Observable<LogicielCaisseEntrepotOption[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService,
    private readonly warehouseService: WarehouseService
  ) {}

  /** GET /api/entrepots/entrepot/:station_id */
  getByStation(): Observable<EntrepotRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${stationId}`;
    return this.http.get<EntrepotsListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.entrepots)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.entrepots;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  /** GET /api/entrepots/entrepot/list/station/:station_id (JWT + station) */
  getEntrepotsErpTableRows(): Observable<EntrepotTableRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/list/station/${stationId}`;
    return this.http.get<EntrepotsErpListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  getEntrepotOptions(): Observable<LogicielCaisseEntrepotOption[]> {
    if (!this.entrepotOptionsCache$) {
      this.entrepotOptionsCache$ = this.getEntrepotsErpTableRows().pipe(
        map((items) =>
          items
            .map((row) => {
              const id = row.id != null ? String(row.id) : '';
              const label =
                row.LIBELLÉ?.trim() ||
                row['CODE ENTREPÔT']?.trim() ||
                (id ? `Entrepôt ${id}` : '');
              return { id, label: label || `Entrepôt ${id}` };
            })
            .filter((o) => o.id !== '')
        ),
        shareReplay(1)
      );
    }
    return this.entrepotOptionsCache$;
  }

  clearEntrepotOptionsCache(): void {
    this.entrepotOptionsCache$ = null;
  }

  /** POST ERP stock + POST logiciel caisse. */
  createEntrepot(body: EntrepotFormPayload): Observable<EntrepotApiRecord> {
    const erpUserId = getErpUserId();
    const erpStationId = getEnterpriseStationIdFromLocalStorage();
    const lcPayload: EntrepotFormPayload = {
      libelle: body.libelle,
      ville: body.ville || '',
      pays: body.pays || '',
      code_entrepot: body.code_entrepot,
      is_reassort: !!body.is_reassort,
      is_globale: !!body.is_globale,
    };

    const erp$ =
      erpUserId != null && erpUserId > 0 && erpStationId > 0
        ? this.warehouseService
            .addWarehouse(
              erpStationId,
              buildErpEntrepotPayloadFromLcForm(lcPayload, erpUserId, erpStationId)
            )
            .pipe(
              map((res) => ({ ok: true as const, res })),
              catchError((err) => of({ ok: false as const, err }))
            )
        : of({ ok: false as const, err: new Error('Session ERP incomplète.') });

    const lc$ = this.createEntrepotLcOnly(lcPayload).pipe(
      map((res) => ({ ok: true as const, res })),
      catchError((err) =>
        of({
          ok: false as const,
          err: err instanceof Error ? err : lcHttpError(err as HttpErrorResponse, 'Erreur LC.'),
        })
      )
    );

    return forkJoin({ erp: erp$, lc: lc$ }).pipe(
      map(({ erp, lc }) => {
        if (!erp.ok && !lc.ok) {
          const erpFail = erp as { ok: false; err: unknown };
          const lcFail = lc as { ok: false; err: unknown };
          const erpErr = erpFail.err as { error?: { message?: string } } | Error | undefined;
          const erpMsg =
            (erpErr && typeof erpErr === 'object' && 'error' in erpErr
              ? erpErr.error?.message
              : undefined) ||
            (erpErr instanceof Error ? erpErr.message : String(erpFail.err ?? 'ERP'));
          const lcMsg =
            lcFail.err instanceof Error ? lcFail.err.message : String(lcFail.err ?? 'caisse');
          throw new Error(`Création entrepôt impossible (ERP et caisse) : ${erpMsg} ; ${lcMsg}`);
        }
        if (!lc.ok) {
          const lcFail = lc as { ok: false; err: unknown };
          throw lcFail.err instanceof Error ? lcFail.err : new Error(String(lcFail.err));
        }
        const lcOk = lc as { ok: true; res: EntrepotApiRecord };
        const lcRecord = { ...lcOk.res };
        if (erp.ok) {
          const erpId = Number(
            (erp.res as { id?: number })?.id ??
              (erp.res as { entrepot?: { id?: number } })?.entrepot?.id
          );
          if (Number.isFinite(erpId) && erpId > 0) {
            lcRecord.erp_id = erpId;
          }
        } else {
          const erpFail = erp as { ok: false; err: unknown };
          console.warn('[createEntrepot] LC OK, ERP non créé', erpFail.err);
        }
        return lcRecord;
      })
    );
  }

  createEntrepotLcOnly(body: EntrepotFormPayload): Observable<EntrepotApiRecord> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot`;
    return this.http.post<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success || res.entrepot == null) {
          throw new Error(res?.error || 'Création impossible');
        }
        this.clearEntrepotOptionsCache();
        const e = res.entrepot as unknown as EntrepotApiRecord;
        if (e.id == null || Number.isNaN(Number(e.id))) {
          throw new Error('Réponse entrepôt invalide (id manquant).');
        }
        return e;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id */
  updateEntrepot(id: string | number, body: EntrepotFormPayload): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${encodeURIComponent(String(id))}`;
    return this.http.put<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour impossible');
        }
        this.clearEntrepotOptionsCache();
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour.'))
      )
    );
  }

  /** GET /api/entrepots/entrepot-caisses/station/:station_id */
  listStationCaissesForAssociation(): Observable<EntrepotCaisseOption[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot-caisses/station/${stationId}`;
    return this.http.get<{ success: boolean; caisses?: EntrepotCaisseOption[] }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.caisses)) {
          throw new Error('Réponse caisses invalide');
        }
        return res.caisses.map((c) => ({
          ...c,
          label: this.formatCaisseOptionLabel(c),
        }));
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des caisses.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id/caisses */
  syncEntrepotCaisses(entrepotId: number, caisseIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${entrepotId}/caisses`;
    return this.http.put<{ success: boolean }>(url, { caisse_ids: caisseIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de l\'association caisse / entrepôt.'))
      )
    );
  }

  formatCaisseOptionLabel(c: EntrepotCaisseOption): string {
    const num = c.numero_de_caisse != null ? `n° ${c.numero_de_caisse}` : `#${c.id}`;
    const wh = c.entrepot_libelle?.trim() || '';
    const configured = c.has_uuid ? ' — configurée' : ' — à initialiser';
    return wh ? `Caisse ${num} (${wh})${configured}` : `Caisse ${num}${configured}`;
  }
}
